import NiceModal from '@ebay/nice-modal-react';
import { any, compose, equals, F, isNil, last, omit, prop } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { SOLUTION_STATE } from 'constants/contacts';
import MODALS from 'constants/modals';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import campaignDetail from 'features/views/campaignManagement/campaignDetail';
// eslint-disable-next-line import/no-cycle
import contacts from 'features/views/campaignManagement/contacts';
import user from 'features/views/user';

import { getSelectedContactType } from '../contacts/selectors';
import { CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME, FIELDS } from '../sidebar/form';
import * as types from './actionTypes';
import { dialogs, processes, progressType } from './constants';
import { getLastRequestBody, getSelectedProcess, getSidebarFormValuesCache } from './selectors';

const { ASSIGN, RELEASE, PRINT } = progressType;
const completeCurrentProgress = createAction(types.COMPLETE_CURRENT_PROGRESS);

const handleResponse = (response) => {
	const { contactCount, processedOk, processedNok } = response;

	const allOK = processedOk === contactCount;
	const partlyOK = processedNok !== contactCount && processedOk !== contactCount;
	const allNOK = processedNok === contactCount;

	if (allNOK) {
		return false;
	} else if (allOK || partlyOK) {
		return true;
	}
};

export const setProgress = createAction(types.SET_PROGRESS, (progress) => ({ progress }));
export const setProcess = createAction(types.SET_PROCESS);
export const setSidebarFormValuesCache = createAction(types.SET_SIDEBAR_FORM_VALUES_CACHE, (formData) => ({
	formData,
}));
export const reset = createAction(types.RESET);

export const saveSidebarFormValuesCache =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME);
		dispatch(setSidebarFormValuesCache(formData));
	};

export const startupCampaignManagementSidebar =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const sidebarFormValuesCache = getSidebarFormValuesCache(getState());
		sendControlNext(CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME, replaceData({ data: sidebarFormValuesCache }));
	};

const checkForErrors = (progressType, processList) => (dispatch) => {
	let currentProcces;
	switch (progressType) {
		case RELEASE:
			currentProcces = last(processList.releaseList);
			return currentProcces.unreleased > 0
				? dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR))
				: null;
		case ASSIGN:
			currentProcces = last(processList.assignList);
			return currentProcces.processedNok > 0
				? dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR))
				: null;
		case PRINT:
			currentProcces = last(processList.printList);
			return currentProcces.processedNok > 0
				? dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR))
				: null;
		default:
			return true;
	}
};

export const runCheckForProgress = (eventPrefix, progressType) => (dispatch) => {
	const checkProgress = () => {
		dispatch(
			fetch.actions.get(fetch.constants.urls.checkContactProcessing, {
				queryParams: eventPrefix ? { eventPrefix } : null,
				onSuccess: [
					(response) => () => {
						const processList = response.body;
						if (!processList) {
							clearInterval(timer);
							return;
						}

						dispatch(checkForErrors(progressType, processList));

						const activeProcesses = ['NEW', 'RUNNING'];
						const runningAssignProcess = processList.assignList.find((p) =>
							activeProcesses.includes(p.state),
						);
						const runningPrintProcess = processList.printList.find((p) =>
							activeProcesses.includes(p.state),
						);
						const runningReleaseProcess = processList.releaseList.find((p) =>
							activeProcesses.includes(p.state),
						);

						const processType = runningAssignProcess ? 'assign' : runningPrintProcess ? 'print' : 'release';
						const runningProcess = runningAssignProcess || runningPrintProcess || runningReleaseProcess;
						const currentProccesNok = compose(
							prop('processedNok'),
							last,
							prop(`${processType}List`),
						)(processList);

						if (!runningProcess) {
							dispatch(handleContactsReload());
							dispatch(completeCurrentProgress());
							clearInterval(timer);

							if (currentProccesNok && !isNil(eventPrefix)) {
								dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
							}

							return;
						}

						dispatch(setProgress({ ...runningProcess, type: processType }));
					},
				],
				onError: [
					() => () => {
						dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
						clearInterval(timer);
					},
				],
			}),
		);
	};

	checkProgress();
	const timer = setInterval(checkProgress, 2000);
};

const handleContactsReload = () => (dispatch, getState) => {
	const wasFetched = fetch.selectors.getWasFetched(fetch.constants.urls.contactList)(getState());
	if (wasFetched) {
		dispatch(contacts.actions.reload());
		dispatch(contacts.actions.resetSelectedContacts());
	}
};

const contactRelease = (campaignId, contactIds) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactReleaseContact, {
			body: {
				campaignWaveId: campaignId,
				contactIds,
				userRole: user.selectors.getCurrentRole(getState()),
			},
			onSuccess: [
				(response) => () => {
					const releasedResult = response.body;
					if (handleResponse(releasedResult)) {
						dispatch(user.actions.fetchAdditionalUserInfo());
						dispatch(handleContactsReload());
						dispatch(runCheckForProgress(response.body.eventPrefix));
					} else {
						dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
					}
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
		}),
	);
};

const contactReleaseBatch = (campaignId) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactReleaseAll, {
			body: {
				filter: {
					campaignId,
					userRole: user.selectors.getCurrentRole(getState()),
					...omit(['onlyToRelease', 'onlyToAssign'], getLastRequestBody(getState())),
				},
			},
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					dispatch(user.actions.fetchAdditionalUserInfo());
					dispatch(runCheckForProgress(response.body.eventPrefix, RELEASE));
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
		}),
	);
};

const setPrint = (campaignId, selectedSubprocess, contactIds) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactSetPrintType, {
			body: {
				campaignWaveId: campaignId,
				printType: selectedSubprocess,
				contactIds,
				userRole: user.selectors.getCurrentRole(getState()),
			},
			onSuccess: [
				(response) => () => {
					const releasedResult = response.body;
					if (handleResponse(releasedResult)) {
						dispatch(user.actions.fetchAdditionalUserInfo());
						dispatch(handleContactsReload());
						dispatch(runCheckForProgress(response.body.eventPrefix));
					} else {
						dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
					}
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
		}),
	);
};

const setPrintBatch = (campaignId, selectedSubprocess) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactSetPrintAll, {
			body: {
				filter: {
					campaignId,
					userRole: user.selectors.getCurrentRole(getState()),
					...omit(['onlyToRelease', 'onlyToAssign'], getLastRequestBody(getState())),
				},
				printTypeId: selectedSubprocess,
			},
			onSuccess: [
				(response) => () => {
					const released = response.body;
					if (released) {
						dispatch(user.actions.fetchAdditionalUserInfo());
						dispatch(runCheckForProgress(response.body.eventPrefix, PRINT));
					} else {
						dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
					}
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
		}),
	);
};

const autoAssign = (campaignId, selectedSubprocess, contactIds) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactAutoAssignContactList, {
			body: {
				campaignWaveId: campaignId,
				algorithm: selectedSubprocess,
				contactIds,
				userRole: user.selectors.getCurrentRole(getState()),
			},
			onSuccess: [
				(response) => () => {
					const releasedResult = response.body;
					if (handleResponse(releasedResult)) {
						dispatch(user.actions.fetchAdditionalUserInfo());
						dispatch(handleContactsReload());
						dispatch(runCheckForProgress(response.body.eventPrefix));
					} else {
						dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
					}
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
		}),
	);
};

const autoAssignBatch = (campaignId, selectedSubprocess) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactAssignAll, {
			body: {
				filter: {
					campaignId,
					userRole: user.selectors.getCurrentRole(getState()),
					...getLastRequestBody(getState()),
				},
				assignAlgorithm: selectedSubprocess,
			},
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					dispatch(user.actions.fetchAdditionalUserInfo());
					dispatch(runCheckForProgress(response.body.eventPrefix, ASSIGN));
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
		}),
	);
};

const resolveManualAssign =
	(campaignId, selectedContactIds) =>
	async (dispatch, getState, { getFormFieldValue }) => {
		const state = getState();
		const channelId = await getFormFieldValue({
			formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
			fieldName: FIELDS.channel,
		});
		const regionId = await getFormFieldValue({
			formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
			fieldName: FIELDS.region,
		});
		const agencyId = await getFormFieldValue({
			formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
			fieldName: FIELDS.agency,
		});
		const manager = await getFormFieldValue({
			formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
			fieldName: FIELDS.manager,
		});
		const seller = await getFormFieldValue({
			formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
			fieldName: FIELDS.seller,
		});
		const ppz = await getFormFieldValue({ formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME, fieldName: FIELDS.ppz });
		const solver = {
			channelId,
			regionId,
			agencyId,
			manager: manager ? { login: manager } : undefined,
			seller: seller ? { login: seller } : undefined,
			ppz: ppz ? { login: ppz } : undefined,
		};
		const selectedContactsTypes = getSelectedContactType(state);

		if (!any(equals(SOLUTION_STATE.ASSIGN), selectedContactsTypes)) {
			if (
				any(equals(SOLUTION_STATE.RELEASE), selectedContactsTypes) ||
				any(equals(SOLUTION_STATE.PROGRESS), selectedContactsTypes)
			) {
				if (!solver.seller) return dispatch(dialog.actions.open(dialogs.NO_SELLER_SELECTED));
			}
		}

		if (!isNil(selectedContactIds)) {
			dispatch(manualAssign(campaignId, solver, selectedContactIds));
		} else {
			dispatch(manualAssignBatch(campaignId, solver));
		}
	};

const manualAssign = (campaignId, solver, contactIds) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactManualAssignContactList, {
			body: {
				campaignWaveId: campaignId,
				userRole: user.selectors.getCurrentRole(getState()),
				contactIds,
				solver,
			},
			onSuccess: [
				(response) => () => {
					const releasedResult = response.body;
					if (handleResponse(releasedResult)) {
						dispatch(user.actions.fetchAdditionalUserInfo());
						dispatch(handleContactsReload());
						dispatch(runCheckForProgress(response.body.eventPrefix));
					} else {
						dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
					}
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
			shouldReportError: F,
		}),
	);
};

const manualAssignBatch = (campaignId, solver) => (dispatch, getState) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.contactAssignAll, {
			body: {
				filter: {
					campaignId,
					userRole: user.selectors.getCurrentRole(getState()),
					...getLastRequestBody(getState()),
				},
				solver,
			},
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					dispatch(user.actions.fetchAdditionalUserInfo());
					dispatch(runCheckForProgress(response.body.eventPrefix, ASSIGN));
				},
			],
			onError: [
				() => () => {
					dispatch(dialog.actions.open(dialogs.CONTACT_PROCESSING_ERROR));
				},
			],
		}),
	);
};

export const execute =
	() =>
	async (dispatch, getState, { getFormFieldValue }) => {
		const state = getState();
		const selectedContactIds = contacts.selectors.getSelectedContactIds(state);
		const selectedProcess = getSelectedProcess(state);
		let selectedSubprocess;

		dispatch(dialog.actions.clear());

		if (selectedContactIds.length === 0) {
			return dispatch(dialog.actions.open(dialogs.NO_CONTACT_SELECTED));
		}

		if (isNilOrEmpty(selectedProcess)) {
			return dispatch(dialog.actions.open(dialogs.NO_PROCESS_SELECTED));
		}

		const campaignId = campaignDetail.selectors.getCampaignId(state);

		switch (selectedProcess) {
			case processes.CONTACT_RELEASE:
				dispatch(contactRelease(campaignId, selectedContactIds));
				break;
			case processes.PRINT_MODE:
				selectedSubprocess = await getFormFieldValue({
					formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
					fieldName: FIELDS.printMode,
				});
				dispatch(setPrint(campaignId, selectedSubprocess, selectedContactIds));
				break;
			case processes.AUTOMATIC_ASSIGN:
				selectedSubprocess = await getFormFieldValue({
					formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
					fieldName: FIELDS.automaticAssign,
				});
				dispatch(autoAssign(campaignId, selectedSubprocess, selectedContactIds));
				break;
			case processes.MANUAL_ASSIGN:
				dispatch(resolveManualAssign(campaignId, selectedContactIds));
				break;
		}
	};

export const executeAll =
	() =>
	async (dispatch, getState, { getFormFieldValue }) => {
		const state = getState();
		const selectedProcess = getSelectedProcess(state);
		const campaignId = campaignDetail.selectors.getCampaignId(state);
		const availableContacts = contacts.selectors.getContacts(state);

		dispatch(dialog.actions.clear());

		if (availableContacts.length === 0) {
			return dispatch(dialog.actions.open(dialogs.NO_CONTACTS_AVAILABLE));
		}

		if (isNilOrEmpty(selectedProcess)) {
			return dispatch(dialog.actions.open(dialogs.NO_PROCESS_SELECTED));
		}

		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('campaignManagement:lightboxTitle'),
			onConfirm: async () => {
				let selectedSubprocess;
				switch (selectedProcess) {
					case processes.CONTACT_RELEASE:
						dispatch(contactReleaseBatch(campaignId));
						break;
					case processes.PRINT_MODE:
						selectedSubprocess = await getFormFieldValue({
							formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
							fieldName: FIELDS.printMode,
						});
						dispatch(setPrintBatch(campaignId, selectedSubprocess));
						break;
					case processes.AUTOMATIC_ASSIGN:
						selectedSubprocess = await getFormFieldValue({
							formName: CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
							fieldName: FIELDS.automaticAssign,
						});
						dispatch(autoAssignBatch(campaignId, selectedSubprocess));
						break;
					case processes.MANUAL_ASSIGN:
						dispatch(resolveManualAssign(campaignId));
						break;
				}
			},
		});
	};
